import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
  Tooltip
} from '@mui/material';
import {
  NavigateBefore,
  NavigateNext
} from '@mui/icons-material';
import { Plus, Trash, X, PencilSimple, Lock, ArrowUp, Calendar } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { format, startOfWeek, endOfWeek, addDays, addWeeks, addMonths, eachDayOfInterval, isSameDay, differenceInMinutes } from 'date-fns';
import api from '../../utils/api';
import { getStaffMemberDisplayPhoto } from '../../utils/avatarConstants';
import { getStaffBackgroundColor, getStaffBorderColor, getStaffDotColor } from '../../utils/staffColors';

// Helper function to format duration
const formatDuration = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) {
    return `${mins}m`;
  }
  if (mins === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${mins}m`;
};

// Helper function to get staff member name
const getStaffMemberName = (staffMember) => {
  if (staffMember.user) {
    const user = staffMember.user;
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || staffMember.name;
  }
  return staffMember.name || 'Unnamed Staff';
};

// Helper function to get staff member initials
const getStaffMemberInitials = (staffMember) => {
  const name = getStaffMemberName(staffMember);
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Helper function to get staff member avatar (account photo first when linked to a user)
const getStaffMemberAvatar = (staffMember) => getStaffMemberDisplayPhoto(staffMember);

// Helper to convert date to datetime-local format
const toIsoLocal = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const pad = (n) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function StaffScheduleGrid({ businessId, staffMembers = [], isFreePlan = false, _onShiftClick, onStaffEdit, onViewCalendar }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [currentWeekStart, setCurrentWeekStart] = useState(() => 
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createShiftOpen, setCreateShiftOpen] = useState(false);
  const [creatingShift, setCreatingShift] = useState(null); // { staffId, day }
  const [editingShift, setEditingShift] = useState(null); // The shift being edited
  const [shiftForm, setShiftForm] = useState({
    start: '',
    end: '',
    repeatPattern: 'none',
    repeatCount: 12,
    breaks: []
  });
  const [savingShift, setSavingShift] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null); // { staffId, day } for hover state
  const [overlapWarning, setOverlapWarning] = useState(null); // { message, onConfirm, onCancel }

  // Calculate week range
  const weekStart = useMemo(() => currentWeekStart, [currentWeekStart]);
  const weekEnd = useMemo(() => endOfWeek(weekStart, { weekStartsOn: 1 }), [weekStart]);
  const weekDays = useMemo(() => 
    eachDayOfInterval({ start: weekStart, end: weekEnd }), 
    [weekStart, weekEnd]
  );

  // Default selected staff on mobile when staff list loads; reset if selected staff removed
  useEffect(() => {
    if (!isMobile) return;
    if (staffMembers.length === 0) {
      setSelectedStaffId('');
      return;
    }
    const exists = staffMembers.some(s => (s._id?.toString() || s._id) === selectedStaffId);
    if (!exists) {
      setSelectedStaffId(staffMembers[0]._id?.toString() || staffMembers[0]._id);
    }
  }, [isMobile, staffMembers, selectedStaffId]);

  // Fetch shifts for the current week
  useEffect(() => {
    if (!businessId) return;

    const fetchShifts = async () => {
      setLoading(true);
      setError('');
      try {
        const startDate = new Date(weekStart);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(weekEnd);
        endDate.setHours(23, 59, 59, 999);
        const response = await api.get(
          `/api/team-member-shifts/business/${businessId}?start=${startDate.toISOString()}&end=${endDate.toISOString()}`
        );
        setShifts(response.data?.shifts || []);
      } catch (err) {
        setError('Failed to load shifts. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchShifts();
  }, [businessId, weekStart, weekEnd]);

  // Navigation handlers
  const handlePrevWeek = () => {
    setCurrentWeekStart(prev => addDays(prev, -7));
  };

  const handleNextWeek = () => {
    setCurrentWeekStart(prev => addDays(prev, 7));
  };

  // Calculate totals per staff member
  const staffTotals = useMemo(() => {
    const totals = {};
    
    staffMembers.forEach(staff => {
      const staffId = staff._id?.toString() || staff._id;
      const staffShifts = shifts.filter(shift => {
        const shiftStaffId = shift.teamMember?._id?.toString() || shift.teamMember?.toString();
        return shiftStaffId === staffId;
      });

      let totalMinutes = 0;
      staffShifts.forEach(shift => {
        const duration = differenceInMinutes(new Date(shift.endTime), new Date(shift.startTime));
        // Subtract break time
        const breakMinutes = (shift.breaks || []).reduce((sum, b) => {
          return sum + differenceInMinutes(new Date(b.endTime), new Date(b.startTime));
        }, 0);
        totalMinutes += (duration - breakMinutes);
      });

      totals[staffId] = {
        totalHours: totalMinutes,
        shiftCount: staffShifts.length
      };
    });

    return totals;
  }, [shifts, staffMembers]);

  // Calculate totals per day
  const dayTotals = useMemo(() => {
    const totals = {};
    
    weekDays.forEach(day => {
      const dayShifts = shifts.filter(shift => {
        const shiftDate = new Date(shift.startTime);
        return isSameDay(shiftDate, day);
      });

      let totalMinutes = 0;
      dayShifts.forEach(shift => {
        const duration = differenceInMinutes(new Date(shift.endTime), new Date(shift.startTime));
        // Subtract break time
        const breakMinutes = (shift.breaks || []).reduce((sum, b) => {
          return sum + differenceInMinutes(new Date(b.endTime), new Date(b.startTime));
        }, 0);
        totalMinutes += (duration - breakMinutes);
      });

      totals[format(day, 'yyyy-MM-dd')] = {
        totalHours: totalMinutes,
        shiftCount: dayShifts.length
      };
    });

    return totals;
  }, [shifts, weekDays]);

  // Get shifts for a specific staff member and day
  const getShiftsForStaffAndDay = (staffId, day) => {
    return shifts.filter(shift => {
      const shiftStaffId = shift.teamMember?._id?.toString() || shift.teamMember?.toString();
      const shiftDate = new Date(shift.startTime);
      return shiftStaffId === staffId && isSameDay(shiftDate, day);
    });
  };

  // Format time range
  const formatTimeRange = (startTime, endTime) => {
    return `${format(new Date(startTime), 'HH:mm')} - ${format(new Date(endTime), 'HH:mm')}`;
  };

  // Handle shift click - open edit modal
  const handleShiftClick = (shift) => {
    setEditingShift(shift);
    setCreatingShift(null);
    setShiftForm({
      start: toIsoLocal(new Date(shift.startTime)),
      end: toIsoLocal(new Date(shift.endTime)),
      repeatPattern: 'none',
      repeatCount: 12,
      breaks: (shift.breaks || []).map((b) => ({
        start: toIsoLocal(new Date(b.startTime)),
        end: toIsoLocal(new Date(b.endTime))
      }))
    });
    setCreateShiftOpen(true);
    setError('');
  };

  // Handle empty cell click - open create shift modal
  const handleEmptyCellClick = (staffId, day) => {
    // Set default times (9 AM to 5 PM)
    const dayStart = new Date(day);
    dayStart.setHours(9, 0, 0, 0);
    const dayEnd = new Date(day);
    dayEnd.setHours(17, 0, 0, 0);
    
    setCreatingShift({ staffId, day });
    setEditingShift(null);
    setShiftForm({
      start: toIsoLocal(dayStart),
      end: toIsoLocal(dayEnd),
      repeatPattern: 'none',
      repeatCount: 12,
      breaks: []
    });
    setCreateShiftOpen(true);
    setError('');
  };

  // Handle cancel/delete shift
  const handleCancelShift = async () => {
    if (!editingShift || !editingShift._id) {
      // If creating a new shift, just close the dialog
      setCreateShiftOpen(false);
      setCreatingShift(null);
      setEditingShift(null);
      setShiftForm({ start: '', end: '', repeatPattern: 'none', repeatCount: 12, breaks: [] });
      setError('');
      return;
    }

    // If editing an existing shift, check if it's part of a series
    if (editingShift.seriesId) {
      setOverlapWarning({
        message: 'This shift is part of a repeating pattern. Delete this shift only, or this shift and all future shifts?',
        onConfirm: (deleteSeries) => {
          setOverlapWarning(null);
          proceedWithDelete(deleteSeries);
        },
        onCancel: () => {
          setOverlapWarning(null);
        },
        isDeleteConfirmation: true
      });
      return;
    }

    proceedWithDelete(false);
  };

  const proceedWithDelete = async (deleteSeries = false) => {
    if (!editingShift || !editingShift._id) return;

    try {
      const url = `/api/team-member-shifts/${editingShift._id}${deleteSeries ? '?deleteSeries=true' : ''}`;
      await api.delete(url);
      
      // Refresh shifts
      const startDate = new Date(weekStart);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(weekEnd);
      endDate.setHours(23, 59, 59, 999);
      
      const response = await api.get(
        `/api/team-member-shifts/business/${businessId}?start=${startDate.toISOString()}&end=${endDate.toISOString()}`
      );
      setShifts(response.data?.shifts || []);

      setCreateShiftOpen(false);
      setCreatingShift(null);
      setEditingShift(null);
      setShiftForm({ start: '', end: '', repeatPattern: 'none', repeatCount: 12, breaks: [] });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel shift');
    }
  };

  // Check for overlapping shifts when creating a repeating pattern
  const checkOverlaps = async (staffId, startTime, endTime, repeatPattern, repeatCount) => {
    if (repeatPattern === 'none') return null;

    const start = new Date(startTime);
    const end = new Date(endTime);
    const duration = end.getTime() - start.getTime();

    // Calculate all dates that will have shifts
    const shiftDates = [];
    let occurrences = 1;
    if (repeatPattern === 'weekly') {
      occurrences = Math.max(1, Math.min(repeatCount, 52));
    } else if (repeatPattern === 'biweekly') {
      occurrences = Math.max(1, Math.min(repeatCount, 26));
    } else if (repeatPattern === 'monthly') {
      occurrences = Math.max(1, Math.min(repeatCount, 12));
    }

    for (let i = 0; i < occurrences; i++) {
      let shiftStart, shiftEnd;
      if (repeatPattern === 'weekly') {
        shiftStart = addWeeks(start, i);
        shiftEnd = new Date(shiftStart.getTime() + duration);
      } else if (repeatPattern === 'biweekly') {
        shiftStart = addWeeks(start, i * 2);
        shiftEnd = new Date(shiftStart.getTime() + duration);
      } else if (repeatPattern === 'monthly') {
        shiftStart = addMonths(start, i);
        shiftEnd = new Date(shiftStart.getTime() + duration);
      } else {
        continue;
      }
      shiftDates.push({ start: shiftStart, end: shiftEnd });
    }

    // Get the date range to check
    const minDate = new Date(Math.min(...shiftDates.map(d => d.start.getTime())));
    const maxDate = new Date(Math.max(...shiftDates.map(d => d.end.getTime())));
    minDate.setHours(0, 0, 0, 0);
    maxDate.setHours(23, 59, 59, 999);

    // Fetch existing shifts for this staff member in the range
    const response = await api.get(
      `/api/team-member-shifts/team-member/${staffId}`,
      {
        params: {
          start: minDate.toISOString(),
          end: maxDate.toISOString()
        }
      }
    );

    const existingShifts = response.data?.shifts || [];
    const overlappingDates = [];

    // Check each planned shift date against existing shifts
    shiftDates.forEach((plannedShift) => {
      const hasOverlap = existingShifts.some((existingShift) => {
        const existingStart = new Date(existingShift.startTime);
        const existingEnd = new Date(existingShift.endTime);
        
        // Check if shifts overlap (they overlap if one starts before the other ends)
        return (
          (plannedShift.start < existingEnd && plannedShift.end > existingStart)
        );
      });

      if (hasOverlap) {
        overlappingDates.push(plannedShift.start);
      }
    });

    if (overlappingDates.length > 0) {
      return {
        count: overlappingDates.length,
        dates: overlappingDates
      };
    }

    return null;
  };

  // Handle save shift (create or update)
  const handleSaveShift = async () => {
    if (!shiftForm.start || !shiftForm.end) {
      setError('Start and end times are required');
      return;
    }

    if (editingShift && !editingShift._id) {
      setError('Shift ID is missing');
      return;
    }

    if (!editingShift && !creatingShift) {
      setError('No staff member selected');
      return;
    }

    setError('');

    // Check for overlaps if creating a repeating shift
    if (!editingShift && shiftForm.repeatPattern !== 'none') {
      const overlaps = await checkOverlaps(
        creatingShift.staffId,
        shiftForm.start,
        shiftForm.end,
        shiftForm.repeatPattern,
        shiftForm.repeatCount
      );

      if (overlaps) {
        setOverlapWarning({
          message: `Warning: This repeating shift will overwrite ${overlaps.count} existing shift${overlaps.count > 1 ? 's' : ''} on the chosen days. Do you want to continue?`,
          onConfirm: () => {
            setOverlapWarning(null);
            proceedWithSave();
          },
          onCancel: () => {
            setOverlapWarning(null);
          }
        });
        return;
      }
    }

    proceedWithSave();
  };

  const proceedWithSave = async () => {
    setSavingShift(true);
    setError('');

    try {
      const startISO = new Date(shiftForm.start).toISOString();
      const endISO = new Date(shiftForm.end).toISOString();

      const payload = {
        startTime: startISO,
        endTime: endISO,
        breaks: shiftForm.breaks.map((b) => ({
          startTime: new Date(b.start).toISOString(),
          endTime: new Date(b.end).toISOString()
        }))
      };

      if (editingShift) {
        // Update existing shift
        await api.put(`/api/team-member-shifts/${editingShift._id}`, payload);
      } else {
        // Create new shift
        payload.teamMemberId = creatingShift.staffId;
        payload.repeatPattern = shiftForm.repeatPattern;
        payload.repeatCount = shiftForm.repeatCount;
        await api.post('/api/team-member-shifts', payload);
      }

      // Refresh shifts
      const startDate = new Date(weekStart);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(weekEnd);
      endDate.setHours(23, 59, 59, 999);
      
      const response = await api.get(
        `/api/team-member-shifts/business/${businessId}?start=${startDate.toISOString()}&end=${endDate.toISOString()}`
      );
      setShifts(response.data?.shifts || []);

      setCreateShiftOpen(false);
      setCreatingShift(null);
      setEditingShift(null);
      setShiftForm({ start: '', end: '', repeatPattern: 'none', repeatCount: 12, breaks: [] });
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Unable to save shift';
      setError(errorMessage);
    } finally {
      setSavingShift(false);
    }
  };

  // Add break
  const addBreak = () => {
    setShiftForm(prev => ({
      ...prev,
      breaks: [...prev.breaks, { start: prev.start, end: prev.start }]
    }));
  };

  // Remove break
  const removeBreak = (idx) => {
    setShiftForm(prev => ({
      ...prev,
      breaks: prev.breaks.filter((_, i) => i !== idx)
    }));
  };

  if (loading && shifts.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ p: 0 }}>
        {/* Header with week navigation */}
        <Box sx={{ 
          p: 2, 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={handlePrevWeek}
                sx={{ minWidth: 44, minHeight: 44, touchAction: 'manipulation' }}
              >
                <NavigateBefore fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleNextWeek}
                sx={{ minWidth: 44, minHeight: 44, touchAction: 'manipulation' }}
              >
                <NavigateNext fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body2" sx={{ ml: 1 }}>
              {format(weekStart, 'MMMM d')} - {format(weekEnd, 'MMMM d, yyyy')}
            </Typography>
          </Box>
          {onViewCalendar && (
            <Tooltip title="View shifts in calendar">
              <IconButton
                size="small"
                onClick={onViewCalendar}
                sx={{ minWidth: 44, minHeight: 44, touchAction: 'manipulation' }}
              >
                <Calendar size={20} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Mobile: vertical layout with staff dropdown */}
        {isMobile ? (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Staff member</InputLabel>
              <Select
                value={selectedStaffId}
                label="Staff member"
                onChange={(e) => setSelectedStaffId(e.target.value)}
                renderValue={(value) => {
                  const staff = staffMembers.find(s => (s._id?.toString() || s._id) === value);
                  return staff ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={getStaffMemberAvatar(staff)}
                        sx={{ width: 28, height: 28, fontSize: '0.75rem' }}
                      >
                        {getStaffMemberInitials(staff)}
                      </Avatar>
                      <Typography variant="body2">{getStaffMemberName(staff)}</Typography>
                    </Box>
                  ) : value;
                }}
              >
                {staffMembers.map((staff) => {
                  const sid = staff._id?.toString() || staff._id;
                  return (
                    <MenuItem key={sid} value={sid}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={getStaffMemberAvatar(staff)}
                          sx={{ width: 28, height: 28, fontSize: '0.75rem' }}
                        >
                          {getStaffMemberInitials(staff)}
                        </Avatar>
                        <Typography variant="body2">{getStaffMemberName(staff)}</Typography>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {staffMembers.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No staff members yet
              </Typography>
            ) : selectedStaffId ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {(() => {
                      const staff = staffMembers.find(s => (s._id?.toString() || s._id) === selectedStaffId);
                      const totals = staff ? (staffTotals[selectedStaffId] || { totalHours: 0, shiftCount: 0 }) : { totalHours: 0, shiftCount: 0 };
                      return `${formatDuration(totals.totalHours)} / ${totals.shiftCount} ${totals.shiftCount === 1 ? 'Shift' : 'Shifts'} this week`;
                    })()}
                  </Typography>
                  {onStaffEdit && (() => {
                    const staff = staffMembers.find(s => (s._id?.toString() || s._id) === selectedStaffId);
                    return staff ? (
                      <IconButton
                        size="small"
                        onClick={() => onStaffEdit(staff)}
                        sx={{ p: 0.5 }}
                      >
                        <PencilSimple size={18} />
                      </IconButton>
                    ) : null;
                  })()}
                </Box>

                <Stack spacing={1.5}>
                  {weekDays.map((day) => {
                    const dayKey = format(day, 'yyyy-MM-dd');
                    const dayShifts = getShiftsForStaffAndDay(selectedStaffId, day);
                    const staffDayMinutes = dayShifts.reduce((sum, shift) => {
                      const dur = differenceInMinutes(new Date(shift.endTime), new Date(shift.startTime));
                      const breakMins = (shift.breaks || []).reduce((b, br) => b + differenceInMinutes(new Date(br.endTime), new Date(br.startTime)), 0);
                      return sum + dur - breakMins;
                    }, 0);
                    return (
                      <Card key={dayKey} variant="outlined" sx={{ overflow: 'visible' }}>
                        <Box
                          sx={{
                            p: 2,
                            minHeight: 56,
                            cursor: dayShifts.length === 0 ? 'pointer' : 'default',
                            touchAction: 'manipulation',
                            '&:hover': dayShifts.length === 0 ? { bgcolor: 'action.hover' } : {},
                            '&:active': dayShifts.length === 0 ? { bgcolor: 'action.selected' } : {}
                          }}
                          onClick={() => {
                            if (dayShifts.length === 0) {
                              handleEmptyCellClick(selectedStaffId, day);
                            }
                          }}
                        >
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            {format(day, 'EEE d MMM')}
                            {dayShifts.length > 0 && (
                              <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
                                ({formatDuration(staffDayMinutes)})
                              </Typography>
                            )}
                          </Typography>
                          {dayShifts.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              Tap to add shift
                            </Typography>
                          ) : (
                            <Stack spacing={0.5}>
                              {dayShifts.map((shift) => {
                                const shiftStaff = staffMembers.find(s => {
                                  const sId = s._id?.toString() || s._id;
                                  const shiftStaffId = shift.teamMember?._id?.toString() || shift.teamMember?.toString();
                                  return sId === shiftStaffId;
                                });
                                const bgColor = shiftStaff ? getStaffBackgroundColor(shiftStaff) : 'action.selected';
                                const borderColor = shiftStaff ? getStaffBorderColor(shiftStaff) : 'divider';
                                return (
                                  <Box
                                    key={shift._id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleShiftClick(shift);
                                    }}
                                    sx={{
                                      p: 1.5,
                                      minHeight: 44,
                                      borderRadius: 1,
                                      backgroundColor: bgColor,
                                      border: '1px solid',
                                      borderColor: borderColor,
                                      cursor: 'pointer',
                                      touchAction: 'manipulation',
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}
                                  >
                                    <Typography variant="body2" fontWeight={500}>
                                      {formatTimeRange(shift.startTime, shift.endTime)}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Stack>
                          )}
                        </Box>
                      </Card>
                    );
                  })}
                </Stack>
              </>
            ) : null}
          </Box>
        ) : (
        /* Desktop: Grid Table */
        (<TableContainer 
          component={Paper} 
          sx={{ 
            overflow: 'auto',
            '& .MuiTable-root': {
              borderCollapse: 'separate',
              borderSpacing: 0
            }
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {/* Staff column header */}
                <TableCell 
                  sx={{ 
                    position: 'sticky', 
                    left: 0, 
                    zIndex: 3,
                    backgroundColor: 'background.paper',
                    minWidth: 200,
                    borderRight: 1,
                    borderColor: 'divider',
                    textAlign: 'left'
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    Staff
                  </Typography>
                </TableCell>
                {/* Day headers */}
                {weekDays.map((day) => {
                  const dayKey = format(day, 'yyyy-MM-dd');
                  const totals = dayTotals[dayKey] || { totalHours: 0, shiftCount: 0 };
                  return (
                    <TableCell 
                      key={dayKey}
                      align="left"
                      sx={{ 
                        minWidth: 150,
                        backgroundColor: 'background.paper'
                      }}
                    >
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="body2" fontWeight={500}>
                          {format(day, 'EEE d MMM')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDuration(totals.totalHours)} / {totals.shiftCount} {totals.shiftCount === 1 ? 'Shift' : 'Shifts'}
                        </Typography>
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {staffMembers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={weekDays.length + 1} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No staff members yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                staffMembers.map((staff) => {
                  const staffId = staff._id?.toString() || staff._id;
                  const totals = staffTotals[staffId] || { totalHours: 0, shiftCount: 0 };
                  const name = getStaffMemberName(staff);
                  const initials = getStaffMemberInitials(staff);
                  const avatar = getStaffMemberAvatar(staff);

                  return (
                    <TableRow key={staffId}>
                      {/* Staff info cell */}
                      <TableCell
                        align="left"
                        sx={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          backgroundColor: 'background.paper',
                          borderRight: 1,
                          borderColor: 'divider'
                        }}
                      >
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1.5,
                            position: 'relative'
                          }}
                        >
                          <Avatar
                            src={avatar}
                            sx={{
                              width: 40,
                              height: 40,
                              fontSize: '0.875rem',
                              fontWeight: 600
                            }}
                          >
                            {initials}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: getStaffDotColor(staff),
                                  flexShrink: 0
                                }}
                              />
                              <Typography variant="body2" fontWeight={500} noWrap>
                                {name}
                              </Typography>
                              {onStaffEdit && (
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    ml: 0.5
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const staff = staffMembers.find(s => (s._id?.toString() || s._id) === staffId);
                                      if (staff) {
                                        onStaffEdit(staff);
                                      }
                                    }}
                                    sx={{
                                      p: 0.5,
                                      opacity: 0.8,
                                      transition: 'opacity 0.2s ease',
                                      '&:hover': {
                                        opacity: 1
                                      }
                                    }}
                                  >
                                    <PencilSimple size={14} />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {formatDuration(totals.totalHours)} / {totals.shiftCount} {totals.shiftCount === 1 ? 'Shift' : 'Shifts'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      {/* Day cells with shifts */}
                      {weekDays.map((day) => {
                        const dayKey = format(day, 'yyyy-MM-dd');
                        const dayShifts = getShiftsForStaffAndDay(staffId, day);
                        
                        return (
                          <TableCell
                            key={dayKey}
                            align="left"
                            sx={{
                              minWidth: 150,
                              height: 80,
                              verticalAlign: 'top',
                              p: 1,
                              borderRight: 1,
                              borderColor: 'divider'
                            }}
                          >
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: 0.5,
                                minHeight: 60,
                                cursor: dayShifts.length === 0 ? 'pointer' : 'default',
                                ...(dayShifts.length === 0 && {
                                  '&:hover': {
                                    backgroundColor: 'action.hover',
                                    borderRadius: 1
                                  }
                                })
                              }}
                              onMouseEnter={() => {
                                if (dayShifts.length === 0) {
                                  setHoveredCell({ staffId, day: dayKey });
                                }
                              }}
                              onMouseLeave={() => {
                                if (dayShifts.length === 0) {
                                  setHoveredCell(null);
                                }
                              }}
                              onClick={() => {
                                if (dayShifts.length === 0) {
                                  handleEmptyCellClick(staffId, day);
                                }
                              }}
                            >
                              {dayShifts.length === 0 ? (
                                <Box sx={{ 
                                  height: 60, 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  color: 'text.secondary',
                                  fontSize: '0.75rem',
                                  opacity: hoveredCell?.staffId === staffId && hoveredCell?.day === dayKey ? 1 : 0,
                                  transition: 'opacity 0.2s ease'
                                }}>
                                  Click to add shift
                                </Box>
                              ) : (
                                dayShifts.map((shift) => {
                                  // Get staff member from the shift
                                  const shiftStaffMember = staffMembers.find(s => {
                                    const sId = s._id?.toString() || s._id;
                                    const shiftStaffId = shift.teamMember?._id?.toString() || shift.teamMember?.toString();
                                    return sId === shiftStaffId;
                                  }) || staff; // Fallback to current staff if not found
                                  
                                  // Use staff-specific colors
                                  const bgColor = getStaffBackgroundColor(shiftStaffMember);
                                  const borderColor = getStaffBorderColor(shiftStaffMember);
                                  
                                  return (
                                    <Box
                                      key={shift._id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleShiftClick(shift);
                                      }}
                                      sx={{
                                        p: 1,
                                        borderRadius: 1,
                                        backgroundColor: bgColor,
                                        border: '1px solid',
                                        borderColor: borderColor,
                                        cursor: 'pointer',
                                        mb: 0.5,
                                        '&:hover': {
                                          opacity: 0.9,
                                          transform: 'translateY(-1px)',
                                          boxShadow: 2
                                        },
                                        transition: 'all 0.2s ease'
                                      }}
                                    >
                                      <Typography 
                                        variant="caption" 
                                        fontWeight={500}
                                        sx={{ fontSize: '0.75rem', color: 'text.primary' }}
                                      >
                                        {formatTimeRange(shift.startTime, shift.endTime)}
                                      </Typography>
                                    </Box>
                                  );
                                })
                              )}
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>)
        )}

        {/* Create/Edit Shift Modal */}
        <Dialog 
          open={createShiftOpen} 
          onClose={() => {
            setCreateShiftOpen(false);
            setCreatingShift(null);
            setEditingShift(null);
            setShiftForm({ start: '', end: '', repeatPattern: 'none', repeatCount: 12, breaks: [] });
            setError('');
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">
                {editingShift 
                  ? 'Edit Shift' 
                  : creatingShift 
                    ? `Add Shift for ${getStaffMemberName(staffMembers.find(s => (s._id?.toString() || s._id) === creatingShift.staffId) || {})}` 
                    : 'Add Shift'}
              </Typography>
              <IconButton
                onClick={() => {
                  setCreateShiftOpen(false);
                  setCreatingShift(null);
                  setEditingShift(null);
                  setShiftForm({ start: '', end: '', repeatPattern: 'none', repeatCount: 12, breaks: [] });
                  setError('');
                }}
                size="small"
              >
                <X size={20} />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Start"
                    type="datetime-local"
                    value={shiftForm.start}
                    onChange={(e) => setShiftForm({ ...shiftForm, start: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="End"
                    type="datetime-local"
                    value={shiftForm.end}
                    onChange={(e) => setShiftForm({ ...shiftForm, end: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
              </Grid>

              {!editingShift && !isFreePlan && (
                <>
                  <FormControl fullWidth>
                    <InputLabel>Repeat pattern</InputLabel>
                    <Select
                      value={shiftForm.repeatPattern}
                      label="Repeat pattern"
                      onChange={(e) => setShiftForm({ ...shiftForm, repeatPattern: e.target.value })}
                    >
                      <MenuItem value="none">Don't repeat</MenuItem>
                      <MenuItem value="weekly">Every week</MenuItem>
                      <MenuItem value="biweekly">Every other week</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>

                  {shiftForm.repeatPattern !== 'none' && (
                    <TextField
                      label={shiftForm.repeatPattern === 'monthly' ? 'Number of months' : 'Number of occurrences'}
                      type="number"
                      inputProps={{ min: 1, max: shiftForm.repeatPattern === 'monthly' ? 12 : 52 }}
                      value={shiftForm.repeatCount}
                      onChange={(e) => setShiftForm({ ...shiftForm, repeatCount: Number(e.target.value) })}
                      helperText={
                        shiftForm.repeatPattern === 'weekly' ? 'How many weeks to repeat' :
                        shiftForm.repeatPattern === 'biweekly' ? 'How many occurrences (every 2 weeks)' :
                        'How many months to repeat'
                      }
                    />
                  )}
                </>
              )}

              {!editingShift && isFreePlan && (
                <Alert
                  severity="info"
                  icon={<Lock size={20} />}
                  action={
                    <Button
                      size="small"
                      color="inherit"
                      endIcon={<ArrowUp size={16} />}
                      onClick={() => navigate('/supplier/billing')}
                    >
                      Upgrade
                    </Button>
                  }
                >
                  Upgrade to use repeating shifts (weekly, biweekly, monthly)
                </Alert>
              )}

              <Divider />

              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2">Breaks</Typography>
                  <Button size="small" startIcon={<Plus size={14} />} onClick={addBreak}>
                    Add break
                  </Button>
                </Box>
                {shiftForm.breaks.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No breaks</Typography>
                )}
                {shiftForm.breaks.map((b, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="center">
                    <TextField
                      label="Start"
                      type="datetime-local"
                      value={b.start}
                      onChange={(e) => {
                        const next = [...shiftForm.breaks];
                        next[idx] = { ...next[idx], start: e.target.value };
                        setShiftForm({ ...shiftForm, breaks: next });
                      }}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                    <TextField
                      label="End"
                      type="datetime-local"
                      value={b.end}
                      onChange={(e) => {
                        const next = [...shiftForm.breaks];
                        next[idx] = { ...next[idx], end: e.target.value };
                        setShiftForm({ ...shiftForm, breaks: next });
                      }}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                    <IconButton onClick={() => removeBreak(idx)} size="small">
                      <Trash size={16} />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            {editingShift && (
              <Button 
                color="error"
                onClick={handleCancelShift}
                sx={{ mr: 'auto' }}
              >
                Cancel Shift
              </Button>
            )}
            <Button 
              onClick={() => {
                setCreateShiftOpen(false);
                setCreatingShift(null);
                setEditingShift(null);
                setShiftForm({ start: '', end: '', repeatPattern: 'none', repeatCount: 12, breaks: [] });
                setError('');
              }}
            >
              Close
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSaveShift} 
              disabled={savingShift}
            >
              {savingShift 
                ? (editingShift ? 'Saving...' : 'Creating...') 
                : (editingShift ? 'Save Changes' : 'Create Shift')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Overlap Warning / Delete Confirmation Dialog */}
        <Dialog open={Boolean(overlapWarning)} onClose={() => overlapWarning?.onCancel()}>
          <DialogTitle>
            {overlapWarning?.isDeleteConfirmation ? 'Delete Shift' : 'Overlap Warning'}
          </DialogTitle>
          <DialogContent>
            <Typography>{overlapWarning?.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => overlapWarning?.onCancel()}>Cancel</Button>
            {overlapWarning?.isDeleteConfirmation ? (
              <>
                <Button
                  onClick={() => overlapWarning?.onConfirm(false)}
                  color="error"
                  variant="outlined"
                >
                  Delete this shift
                </Button>
                <Button
                  onClick={() => overlapWarning?.onConfirm(true)}
                  color="error"
                  variant="contained"
                >
                  Delete this and future shifts
                </Button>
              </>
            ) : (
              <Button
                onClick={() => overlapWarning?.onConfirm()}
                variant="contained"
                color="warning"
              >
                Continue Anyway
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default StaffScheduleGrid;

